/**
 * «По целям на культуры» — собрать план от целевой структуры площадей
 * (AK-791 этап А, макет `11-auto-sowing-plan.html`, утв. founder 06.09).
 *
 * Два прежних способа идут ОТ ПОЛЯ. Этот — ход руководителя: «в 2027-м хочу
 * 45 % озимой пшеницы и 8 250 га подсолнечника», а система разносит это по
 * полям, соблюдая правила севооборота этого варианта.
 *
 * Три шага и почему их три
 * ------------------------
 * цель → предпросмотр → запись. Средний шаг не формальность: раскладка трогает
 * сотни строк, и «нажал — посмотрел, что вышло» означало бы ручную чистку
 * семисот полей при первом же неудачном прогоне.
 *
 * Единица (га ⇄ %) переключается НА ВСЮ таблицу: построчный выбор заставил бы
 * агронома спорить с округлением в каждой строке. Знаменатель процента — та же
 * учётная пашня варианта, что в СПП и сводке, иначе «итого 103 %» появилось бы
 * из ничего.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  isStalePreview,
  rowHa,
  targetsBalance,
  toTargetsPayload,
  useApplyAutoBuild,
  useAutoBuildPreview,
  useLastAutoBuild,
  useTargetDefaults,
  type AutoBuildApplyResult,
  type AutoBuildPreview,
  type TargetRow,
  type TargetUnit,
} from '@/lib/planning/autoBuild';
import { formatHa } from '@/lib/planning/planVariant';
import { usePlanRules } from '@/lib/planning/rotationRules';
import { AutoBuildPreviewPanel, NoRulesHint } from './AutoBuildPreviewPanel';

const STALE_TEXT =
  'Данные плана изменились с момента предпросмотра — раскладка пересчитана, посмотрите её заново.';

let rowSeq = 0;
const nextKey = () => `target-${(rowSeq += 1)}`;

function emptyRow(): TargetRow {
  return { key: nextKey(), cropId: '', input: '' };
}

/** Гектары формы → текст поля ввода в текущей единице. */
function inputFor(ha: number, unit: TargetUnit, arableHa: number): string {
  if (unit === 'ha') return String(Math.round(ha));
  if (arableHa <= 0) return '';
  return ((ha / arableHa) * 100).toFixed(1).replace('.', ',');
}

export function TargetFillWizard({
  scenarioId,
  variantStatus,
  arableHa: arableFromRows,
  crops,
  canSow,
  onClose,
}: {
  scenarioId: string;
  variantStatus: string;
  /**
   * Учётная пашня варианта, посчитанная по строкам экрана, — знаменатель
   * процентов и потолок цели ДО первого расчёта. Как только раскладка
   * посчитана, берём пашню из ответа: считает её сервер (тот же источник, что
   * у СПП), и расхождение до гектара сделало бы кнопку «Показать раскладку»
   * погашенной там, где ручка посчитала бы — и наоборот.
   */
  arableHa: number;
  crops: { id: string; name: string }[];
  canSow: boolean;
  onClose: () => void;
}) {
  const [unit, setUnit] = useState<TargetUnit>('ha');
  const [rows, setRows] = useState<TargetRow[]>([emptyRow()]);
  const [preview, setPreview] = useState<AutoBuildPreview | null>(null);
  const [result, setResult] = useState<AutoBuildApplyResult | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  // Предзаполнение — ОДИН раз: иначе ответ ручки, приехавший после первой
  // правки, затирал бы уже введённую цель.
  const prefilled = useRef(false);

  const [serverArableHa, setServerArableHa] = useState<number | null>(null);
  const arableHa = serverArableHa ?? arableFromRows;

  const lastRun = useLastAutoBuild(scenarioId);
  const defaults = useTargetDefaults(scenarioId);
  const previewMut = useAutoBuildPreview();
  const applyMut = useApplyAutoBuild();
  // AK-809: без правил севооборота раскладка ГАРАНТИРОВАННО пустая — движок
  // спрашивает совместимость культуры с полем только у них
  // (`rotation_rules.suggest_for_field`, ADR-075 §D6). Проверяем ДО расчёта,
  // а не показываем «0 из 800 полей» после — черновик без правил (например,
  // свежая копия утверждённого варианта: `duplicate` их не копирует) не
  // сигнал агроному ни о чём, кроме потраченного клика.
  const rulesQuery = usePlanRules(scenarioId);
  const rulesKnown = !rulesQuery.isLoading && rulesQuery.data !== undefined;
  const noRules = rulesKnown && (rulesQuery.data ?? []).length === 0;

  const cropName = (id: string) => crops.find((c) => c.id === id)?.name ?? 'культура';
  const factByCrop = useMemo(
    () => new Map((defaults.data ?? []).map((d) => [d.crop_id, d.fact_ha])),
    [defaults.data],
  );

  const fillFromFact = () => {
    const fact = defaults.data ?? [];
    if (fact.length === 0) return;
    setRows(
      fact.map((d) => ({
        key: nextKey(),
        cropId: d.crop_id,
        input: inputFor(d.fact_ha, unit, arableHa),
      })),
    );
  };

  // Форма открывается на последнем прогоне варианта, иначе на факте прошлого
  // сезона, иначе пустая (пустая — валидное состояние, FR-4).
  useEffect(() => {
    if (prefilled.current) return;
    if (lastRun.isLoading || defaults.isLoading) return;
    const fromRun = lastRun.data?.targets ?? [];
    if (fromRun.length > 0) {
      prefilled.current = true;
      setRows(
        [...fromRun]
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((t) => ({
            key: nextKey(),
            cropId: t.crop_id,
            input: inputFor(t.target_ha, 'ha', arableHa),
          })),
      );
      return;
    }
    const fact = defaults.data ?? [];
    if (fact.length > 0) {
      prefilled.current = true;
      setRows(
        fact.map((d) => ({
          key: nextKey(),
          cropId: d.crop_id,
          input: inputFor(d.fact_ha, 'ha', arableHa),
        })),
      );
    }
  }, [lastRun.isLoading, lastRun.data, defaults.isLoading, defaults.data, arableHa]);

  const balance = targetsBalance(rows, unit, arableHa);
  const payload = () => toTargetsPayload(rows, unit, arableHa);

  const switchUnit = (next: TargetUnit) => {
    if (next === unit) return;
    // Пересчитываем ВВЕДЁННОЕ, а не обнуляем: смена единицы — это способ
    // посмотреть на ту же цель другими глазами, а не начать заново.
    setRows((prev) =>
      prev.map((r) => ({ ...r, input: inputFor(rowHa(r, unit, arableHa), next, arableHa) })),
    );
    setUnit(next);
  };

  const runPreview = () => {
    setStale(null);
    previewMut.mutate(
      { scenarioId, targets: payload(), unit },
      {
        onSuccess: (data) => {
          setPreview(data);
          setServerArableHa(data.arable_ha);
        },
      },
    );
  };

  const runApply = () => {
    if (!preview) return;
    applyMut.mutate(
      { scenarioId, targets: payload(), unit, digest: preview.digest },
      {
        onSuccess: (data) => setResult(data),
        onError: (err) => {
          if (!isStalePreview(err)) return;
          // Конфликт «данные изменились» лечится сам: показываем свежую
          // раскладку и объясняем словами, а не оставляем человека с ошибкой.
          setStale(STALE_TEXT);
          previewMut.mutate(
            { scenarioId, targets: payload(), unit },
            { onSuccess: (data) => setPreview(data) },
          );
        },
      },
    );
  };

  if (result) {
    return (
      <Stack gap={3}>
        <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
          Записано: <span className="font-medium">{result.applied_fields}</span> полей
          собрано автоматически ({formatHa(result.applied_ha)}). Ручные строки не тронуты (
          {result.manual_untouched_fields}).{' '}
          {result.unresolved_fields > 0 &&
            `Не разложилось ${result.unresolved_fields} полей — их разбираете вы.`}
        </div>
        <p className="text-sm text-muted-foreground">
          Строки помечены «собрано автоматически». Повторный сбор перезапишет только их —
          всё, к чему вы прикоснулись руками, автоматика обходит.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={onClose}>
            Готово
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setResult(null);
              setPreview(null);
              setStale(null);
            }}
          >
            Задать цель заново
          </Button>
        </div>
      </Stack>
    );
  }

  if (preview) {
    return (
      <AutoBuildPreviewPanel
        preview={preview}
        applying={applyMut.isPending || previewMut.isPending}
        staleMessage={stale}
        onApply={runApply}
        onEditTargets={() => {
          setPreview(null);
          setStale(null);
        }}
        onRebuild={runPreview}
        onCancel={onClose}
      />
    );
  }

  return (
    <Stack gap={3}>
      <p className="text-sm text-muted-foreground">
        Какую структуру площадей хотим получить. Пашня хозяйства —{' '}
        <span className="font-medium text-foreground">{formatHa(arableHa)}</span> (учётная
        площадь полей в севообороте). Пар в цель не входит: пар — состояние поля, его
        назначаете вы.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1" role="group" aria-label="Единица цели">
          <Button
            type="button"
            size="sm"
            variant={unit === 'ha' ? 'default' : 'outline'}
            aria-pressed={unit === 'ha'}
            onClick={() => switchUnit('ha')}
          >
            гектары
          </Button>
          <Button
            type="button"
            size="sm"
            variant={unit === 'pct' ? 'default' : 'outline'}
            aria-pressed={unit === 'pct'}
            onClick={() => switchUnit('pct')}
          >
            проценты
          </Button>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={(defaults.data ?? []).length === 0}
          onClick={fillFromFact}
        >
          Взять из прошлого сезона
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setRows([emptyRow()])}
        >
          Очистить
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={() => setRows((prev) => [...prev, emptyRow()])}
        >
          Добавить культуру
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Культура</TableHead>
              <TableHead className="min-w-[140px]">
                Цель, {unit === 'ha' ? 'га' : '% пашни'}
              </TableHead>
              <TableHead className="text-right">
                {unit === 'ha' ? 'Доля пашни' : 'Гектаров'}
              </TableHead>
              <TableHead className="text-right">Было в прошлом сезоне</TableHead>
              <TableHead className="text-right">Изменение</TableHead>
              <TableHead className="text-right">Действие</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const ha = row.cropId ? rowHa(row, unit, arableHa) : 0;
              const fact = row.cropId ? factByCrop.get(row.cropId) : undefined;
              const taken = new Set(
                rows.filter((r) => r.key !== row.key).map((r) => r.cropId),
              );
              return (
                <TableRow key={row.key}>
                  <TableCell>
                    <Select
                      value={row.cropId || undefined}
                      onValueChange={(v) =>
                        setRows((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, cropId: v } : r)),
                        )
                      }
                    >
                      <SelectTrigger aria-label={`Культура в цели, строка ${index + 1}`}>
                        <SelectValue placeholder="выберите культуру" />
                      </SelectTrigger>
                      <SelectContent>
                        {crops.map((c) => (
                          <SelectItem key={c.id} value={c.id} disabled={taken.has(c.id)}>
                            {c.name}
                            {taken.has(c.id) ? ' — уже в таблице' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Label className="sr-only" htmlFor={`target-${row.key}`}>
                      Цель по культуре {row.cropId ? cropName(row.cropId) : index + 1}
                    </Label>
                    <Input
                      id={`target-${row.key}`}
                      inputMode="decimal"
                      value={row.input}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key ? { ...r, input: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {unit === 'ha'
                      ? arableHa > 0
                        ? `${((ha / arableHa) * 100).toFixed(1)} %`
                        : '—'
                      : formatHa(ha)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {fact === undefined ? '—' : formatHa(fact)}
                  </TableCell>
                  <TableCell className="text-right">
                    {fact === undefined
                      ? '—'
                      : `${ha - fact >= 0 ? '+' : '−'}${formatHa(Math.abs(ha - fact))}`}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setRows((prev) => prev.filter((r) => r.key !== row.key))
                      }
                    >
                      Убрать
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3 text-sm">
        <span className="font-medium">Итого цель: {formatHa(balance.totalHa)}</span>
        <span className="text-muted-foreground">{balance.pct.toFixed(1)} % пашни</span>
        {balance.overshootHa > 0 ? (
          <Badge variant="destructive">перебор {formatHa(balance.overshootHa)}</Badge>
        ) : balance.freeHa > 0.5 ? (
          <Badge variant="outline">свободно останется {formatHa(balance.freeHa)}</Badge>
        ) : (
          <Badge variant="primary">сходится с пашней</Badge>
        )}
      </div>

      {balance.overshootHa > 0 && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          Целевые площади больше пашни на {formatHa(balance.overshootHa)}. Пашня хозяйства —{' '}
          {formatHa(arableHa)}, в таблице набрано {formatHa(balance.totalHa)}. Уберите лишнее
          или уменьшите цель по культурам.
        </div>
      )}
      {balance.overshootHa === 0 && balance.freeHa > 0.5 && (
        <p className="text-sm text-muted-foreground">
          Цель покрывает не всю пашню: свободно останется {formatHa(balance.freeHa)}. Это
          нормально, если часть полей вы распределяете руками — такие поля так и останутся
          без культуры, культуру сверх цели автосбор не добирает.
        </p>
      )}

      {noRules && <NoRulesHint />}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Button
          type="button"
          disabled={!balance.canPreview || previewMut.isPending || !canSow || noRules}
          onClick={runPreview}
        >
          {previewMut.isPending ? 'Считаем раскладку…' : 'Показать раскладку'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Отменить
        </Button>
        <span className="text-xs text-muted-foreground">
          {variantStatus === 'draft'
            ? 'Ничего не запишется, пока вы не посмотрите раскладку и не нажмёте «Записать в черновик».'
            : 'Вариант утверждён как рабочий бюджет — автосбор его не переписывает.'}
        </span>
      </div>
    </Stack>
  );
}
